import { useState } from "react";
import {
  Mail,
  User,
  MessageCircle,
  Loader2,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { motion } from "framer-motion";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null); // SUCCESS | ERROR
  const [loading, setLoading] = useState(false);

  const validate = () => {
    let newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required.";
    if (!formData.email.trim()) newErrors.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Enter a valid email address.";
    if (!formData.message.trim())
      newErrors.message = "Message cannot be empty.";
    else if (formData.message.length < 10)
      newErrors.message = "Message must be at least 10 characters.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await fetch("https://formspree.io/f/xeoyjbdk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        setStatus("SUCCESS");
        setFormData({ name: "", email: "", message: "" });
      } else {
        setStatus("ERROR");
      }
    } catch (err) {
      setStatus("ERROR");
      console.error(err);
    } finally {
      setLoading(false);
      setTimeout(() => setStatus(null), 2500);
    }
  };

  return (
    <motion.div
      id="contact"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0, transition: { duration: 0.6 } }}
      viewport={{ once: true, amount: 0.2 }}
      className="w-full max-w-xl mx-auto mt-20 px-4 sm:px-6"
    >
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
          Contact
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mt-2">
          Get in touch with me. I will get back to you as soon as possible.
        </p>
      </div>

      {/* Divider Line */}
      <div className="flex justify-center mt-6 mb-10">
        <div className="w-24 h-[3px] bg-gradient-to-r from-gray-400 to-gray-600 dark:from-gray-600 dark:to-gray-300 rounded-full"></div>
      </div>

      {/* Form container */}
      <form className="space-y-6" onSubmit={handleSubmit}>
        {/* Form title */}
        <div className="mb-4 text-start">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Send me a message
          </h3>
          <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
            Fill out the form below and I will get back to you as soon as
            possible.
          </p>
        </div>

        {/* Success / Error Messages */}
        {status === "SUCCESS" && (
          <div className="flex items-center gap-2 text-green-700 dark:text-green-300 text-sm bg-green-100 dark:bg-green-800/40 px-3 py-2 rounded">
            <CheckCircle2 size={16} /> Message sent successfully!
          </div>
        )}
        {status === "ERROR" && (
          <div className="flex items-center gap-2 text-red-700 dark:text-red-300 text-sm bg-red-100 dark:bg-red-800/40 px-3 py-2 rounded">
            <XCircle size={16} /> Failed to send message. Try again.
          </div>
        )}

        {/* Name */}
        <div>
          <label className="text-xs uppercase tracking-wide text-gray-600 dark:text-gray-300">
            Full Name
          </label>
          <div
            className={`flex items-center gap-3 px-3 py-2 border rounded-md
            ${
              errors.name
                ? "border-red-400 dark:border-red-500"
                : "border-gray-300 dark:border-gray-600"
            }`}
          >
            <User size={18} className="text-teal-500" />
            <input
              type="text"
              placeholder="John Doe"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full bg-transparent focus:outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-300"
            />
          </div>
          {errors.name && (
            <p className="text-red-500 dark:text-red-400 text-xs mt-1">
              {errors.name}
            </p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="text-xs uppercase tracking-wide text-gray-600 dark:text-gray-300">
            Email Address
          </label>
          <div
            className={`flex items-center gap-3 px-3 py-2 border rounded-md
            ${
              errors.email
                ? "border-red-400 dark:border-red-500"
                : "border-gray-300 dark:border-gray-600"
            }`}
          >
            <Mail size={18} className="text-blue-500" />
            <input
              type="email"
              placeholder="example@mail.com"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className="w-full bg-transparent focus:outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-300"
            />
          </div>
          {errors.email && (
            <p className="text-red-500 dark:text-red-400 text-xs mt-1">
              {errors.email}
            </p>
          )}
        </div>

        {/* Message */}
        <div>
          <label className="text-xs uppercase tracking-wide text-gray-600 dark:text-gray-300">
            Message
          </label>
          <div
            className={`flex items-start gap-3 px-3 py-2 border rounded-md
            ${
              errors.message
                ? "border-red-400 dark:border-red-500"
                : "border-gray-300 dark:border-gray-600"
            }`}
          >
            <MessageCircle size={18} className="text-purple-500 mt-1" />
            <textarea
              rows="4"
              placeholder="Write your message..."
              value={formData.message}
              onChange={(e) =>
                setFormData({ ...formData, message: e.target.value })
              }
              className="w-full bg-transparent focus:outline-none text-sm resize-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-300"
            />
          </div>
          {errors.message && (
            <p className="text-red-500 dark:text-red-400 text-xs mt-1">
              {errors.message}
            </p>
          )}
        </div>

        {/* Submit */}
        <motion.button
          whileTap={{ scale: 0.97 }}
          whileHover={{ scale: 1.01 }}
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-black-100 dark:bg-black-800 text-black dark:text-white font-semibold rounded-lg border border-gray-300 dark:border-gray-600 transition hover:opacity-90"
        >
          {loading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            "Send Message"
          )}
        </motion.button>
      </form>
    </motion.div>
  );
};

export default ContactSection;
